import styled, { keyframes, css } from 'styled-components';

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(-8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const spin = keyframes`
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
`;

export const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  padding-top: 3rem;
  padding-bottom: 3rem;
  background: linear-gradient(135deg, #EFF6FF 0%, #F3E8FF 50%, #FCE7F3 100%);
`;

export const ContentWrapper = styled.div`
  max-width: 56rem;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

export const Card = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(12px);
  border-radius: 1rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
  padding: 2rem;
  border: 1px solid rgba(229, 231, 235, 0.5);
`;

export const Header = styled.div`
  text-align: center;
  margin-bottom: 1.5rem;
`;

export const IconWrapper = styled.div<{ $color?: 'blue' | 'purple' }>`
  display: inline-flex;
  padding: 0.75rem;
  border-radius: 9999px;
  margin-bottom: 0.5rem;
  background: ${props => props.$color === 'purple' ? '#F3E8FF' : '#DBEAFE'};

  svg {
    width: 1.5rem;
    height: 1.5rem;
    color: ${props => props.$color === 'purple' ? '#9333EA' : '#2563EB'};
  }
`;

export const Title = styled.h1<{ $gradient?: string }>`
  font-size: 1.875rem;
  font-weight: 700;
  margin: 0.5rem 0;
  letter-spacing: -0.025em;
  background: ${props => props.$gradient || 'linear-gradient(to right, #2563EB, #9333EA)'};
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

export const Subtitle = styled.p`
  color: #4B5563;
  margin: 0.5rem 0 0 0;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  margin-bottom: 1.5rem;
`;

export const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 600;
  color: #374151;
`;

export const InputWrapper = styled.div`
  position: relative;
`;

export const Input = styled.input`
  width: 100%;
  padding: 0.875rem 1rem;
  padding-right: 3rem;
  border: 2px solid #E5E7EB;
  border-radius: 0.75rem;
  font-size: 1rem;
  outline: none;
  transition: all 0.2s;

  &:focus {
    border-color: #3B82F6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
  }

  &::placeholder {
    color: #9CA3AF;
  }

  &[data-mono="true"] {
    font-family: 'Monaco', 'Courier New', monospace;
  }
`;

export const InputIcon = styled.div`
  position: absolute;
  right: 0.75rem;
  top: 50%;
  transform: translateY(-50%);
  color: #9CA3AF;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }
`;

export const Button = styled.button<{ $gradient?: string; $disabled?: boolean }>`
  width: 100%;
  padding: 1rem;
  border-radius: 0.75rem;
  font-weight: 600;
  font-size: 1rem;
  border: none;
  cursor: ${props => props.$disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  box-shadow: ${props => props.$disabled ? 'none' : '0 10px 15px -3px rgba(0, 0, 0, 0.1)'};
  background: ${props => props.$disabled 
    ? 'linear-gradient(to right, #9CA3AF, #6B7280)' 
    : props.$gradient || 'linear-gradient(to right, #2563EB, #9333EA)'};
  color: white;

  &:hover {
    ${props => !props.$disabled && `
      background: linear-gradient(to right, #1D4ED8, #7E22CE);
      transform: scale(1.02);
    `}
  }

  &:active {
    ${props => !props.$disabled && `
      transform: scale(0.98);
    `}
  }

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  svg {
    width: 1.25rem;
    height: 1.25rem;

    &.spinning {
      animation: ${spin} 1s linear infinite;
    }
  }
`;

export const Alert = styled.div<{ $type?: 'error' | 'success' | 'info' }>`
  padding: 1rem;
  border-radius: 0.5rem;
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  margin-top: 1.5rem;
  ${props => {
    if (props.$type === 'error') {
      return css`
        background: #FEF2F2;
        border-left: 4px solid #EF4444;
      `;
    } else if (props.$type === 'success') {
      return css`
        background: linear-gradient(to right, #F0FDF4, #ECFDF5);
        border: 2px solid #86EFAC;
        animation: ${slideIn} 0.3s ease-out;
      `;
    } else if (props.$type === 'info') {
      return css`
        background: linear-gradient(to right, #F3E8FF, #FCE7F3);
        border: 2px solid #D8B4FE;
        animation: ${slideIn} 0.3s ease-out;
      `;
    }
  }}

  svg {
    width: 1.25rem;
    height: 1.25rem;
    flex-shrink: 0;
    margin-top: 0.125rem;
    color: ${props => {
      if (props.$type === 'error') return '#EF4444';
      if (props.$type === 'success') return '#16A34A';
      if (props.$type === 'info') return '#9333EA';
      return '#4B5563';
    }};
  }

  p {
    margin: 0;
    font-size: 0.875rem;
    color: ${props => {
      if (props.$type === 'error') return '#991B1B';
      if (props.$type === 'success') return '#166534';
      if (props.$type === 'info') return '#6B21A8';
      return '#374151';
    }};
  }
`;

export const ResultBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

export const ResultHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: #16A34A;
  font-weight: 600;

  svg {
    width: 1.25rem;
    height: 1.25rem;
  }

  &[data-purple="true"] {
    color: #9333EA;
  }
`;

export const ResultInput = styled.div`
  display: flex;
  gap: 0.5rem;

  input {
    flex: 1;
    padding: 0.75rem 1rem;
    background: white;
    border: 2px solid #86EFAC;
    border-radius: 0.5rem;
    font-size: 0.875rem;
    font-family: 'Monaco', 'Courier New', monospace;
    outline: none;

    &:focus {
      box-shadow: 0 0 0 3px rgba(34, 197, 94, 0.1);
    }

    &[data-purple="true"] {
      border-color: #D8B4FE;

      &:focus {
        box-shadow: 0 0 0 3px rgba(147, 51, 234, 0.1);
      }
    }
  }

  button {
    padding: 0.75rem 1.5rem;
    background: #16A34A;
    color: #ffffff;
    border: none;
    border-radius: 0.5rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
    -webkit-font-smoothing: antialiased;
    -moz-osx-font-smoothing: grayscale;

    &:hover {
      background: #15803D;
      box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
    }

    &:active {
      transform: scale(0.98);
    }

    svg {
      width: 1rem;
      height: 1rem;
      flex-shrink: 0;
      color: #ffffff;
      opacity: 1;
    }
  }
`;

export const LoadingContainer = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;

  p {
    color: #4B5563;
  }
`;

export const ResultDetail = styled.div`
  padding: 0.75rem;
  background: white;
  border-radius: 0.5rem;
  font-size: 0.875rem;

  strong {
    color: #6B21A8;
  }

  a {
    color: #2563EB;
    text-decoration: none;
    word-break: break-all;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(1rem);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const fadeOut = keyframes`
  from {
    opacity: 1;
    transform: translateY(0);
  }
  to {
    opacity: 0;
    transform: translateY(-0.5rem);
  }
`;

export const Toast = styled.div<{ $isVisible: boolean }>`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: #1F2937;
  color: white;
  padding: 1rem 1.5rem;
  border-radius: 0.75rem;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  font-size: 0.875rem;
  font-weight: 500;
  z-index: 9999;
  animation: ${props => props.$isVisible ? css`${fadeInUp} 0.3s ease-out` : css`${fadeOut} 0.2s ease-in`};
  pointer-events: ${props => props.$isVisible ? 'auto' : 'none'};

  svg {
    width: 1.25rem;
    height: 1.25rem;
    color: #10B981;
    flex-shrink: 0;
  }
`;
